/**
 * Engagement Tracking Service
 *
 * Tracks user engagement metrics:
 * - Opens: Total app opens
 * - Messages: Total messages sent
 *
 * Reports threshold events when combinations are reached:
 * 1o1m, 1o2m, 1o3m, 2o1m, 2o2m, 2o3m, 3o1m, 3o2m, 3o3m
 *
 * Stops tracking after 3o3m is reached.
 */

const STORAGE_KEY = 'taka_engagement'

// All possible thresholds: {opens}o{messages}m
const ALL_THRESHOLDS = [
  '1o1m',
  '1o2m',
  '1o3m',
  '2o1m',
  '2o2m',
  '2o3m',
  '3o1m',
  '3o2m',
  '3o3m',
] as const

export type EngagementThreshold = (typeof ALL_THRESHOLDS)[number]

interface EngagementState {
  openCount: number
  messageCount: number
  reportedThresholds: EngagementThreshold[]
}

/**
 * Create a fresh state
 */
function createFreshState(): EngagementState {
  return {
    openCount: 0,
    messageCount: 0,
    reportedThresholds: [],
  }
}

/**
 * Check if tracking is complete (3o3m reached)
 */
function isTrackingComplete(state: EngagementState): boolean {
  return state.reportedThresholds.includes('3o3m')
}

/**
 * Load state from localStorage
 */
function loadState(): EngagementState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return createFreshState()
    }

    const state: EngagementState = JSON.parse(stored)
    return state
  } catch {
    return createFreshState()
  }
}

/**
 * Save state to localStorage
 */
function saveState(state: EngagementState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    console.warn('Failed to save engagement state to localStorage')
  }
}

/**
 * Check which thresholds are newly reached based on current state
 * Returns only thresholds that haven't been reported yet
 */
function getNewlyReachedThresholds(state: EngagementState): EngagementThreshold[] {
  const { openCount, messageCount } = state
  const newThresholds: EngagementThreshold[] = []

  for (const threshold of ALL_THRESHOLDS) {
    // Skip if already reported
    if (state.reportedThresholds.includes(threshold)) {
      continue
    }

    // Parse threshold (e.g., "2o3m" -> opens=2, messages=3)
    const match = threshold.match(/^(\d+)o(\d+)m$/)
    if (!match) continue

    const requiredOpens = parseInt(match[1], 10)
    const requiredMessages = parseInt(match[2], 10)

    // Check if threshold is reached
    if (openCount >= requiredOpens && messageCount >= requiredMessages) {
      newThresholds.push(threshold)
    }
  }

  return newThresholds
}

export interface RecordResult {
  newThresholds: EngagementThreshold[]
  state: EngagementState
}

/**
 * Record an app open and return newly crossed thresholds
 */
export function recordOpen(): RecordResult {
  const state = loadState()

  // Don't track if 3o3m already reached
  if (isTrackingComplete(state)) {
    return { newThresholds: [], state }
  }

  // Increment open count
  state.openCount++

  // Check for newly reached thresholds
  const newThresholds = getNewlyReachedThresholds(state)

  // Mark new thresholds as reported
  state.reportedThresholds.push(...newThresholds)

  // Save updated state
  saveState(state)

  return { newThresholds, state }
}

/**
 * Record a message sent and return newly crossed thresholds
 */
export function recordMessage(): RecordResult {
  const state = loadState()

  // Don't track if 3o3m already reached
  if (isTrackingComplete(state)) {
    return { newThresholds: [], state }
  }

  // Increment message count
  state.messageCount++

  // Check for newly reached thresholds
  const newThresholds = getNewlyReachedThresholds(state)

  // Mark new thresholds as reported
  state.reportedThresholds.push(...newThresholds)

  // Save updated state
  saveState(state)

  return { newThresholds, state }
}

/**
 * Get current state (for debugging/testing)
 */
export function getState(): EngagementState {
  return loadState()
}

/**
 * Reset state (for testing purposes)
 */
export function resetState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Silently fail
  }
}

export const weeklyEngagementService = {
  recordOpen,
  recordMessage,
  getState,
  resetState,
}
